import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useClinicContext } from './useClinicContext';
import {
  fetchRezeptApplicationCountOne,
  createRezeptApplicationCount,
  updateRezeptApplicationCount,
  deleteRezeptApplicationCount,
  type RezeptApplicationCountPayload,
} from '@/services/rezeptApplicationCountService';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage } from '@/utils/apiError';

/** Convert rezept_yearmonth (YYYYMM) to YYYY-MM for month picker */
export function yearMonthToStr(ym: number): string {
  if (!ym || ym < 200001) return '';
  const y = Math.floor(ym / 100);
  const m = ym % 100;
  return `${y}-${String(m).padStart(2, '0')}`;
}

/** Convert YYYY-MM string to YYYYMM number */
export function strToYearMonth(s: string): number {
  if (!s || !/^\d{4}-\d{2}$/.test(s)) return 0;
  const [y, m] = s.split('-').map(Number);
  return y * 100 + m;
}

export function useRezeptApplicationCountOne(patientId: string | undefined, id: string | undefined) {
  const { tenant, clinicNumber } = useClinicContext();

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['rezept-application-counts', 'one', tenant, clinicNumber, patientId, id],
    queryFn: () =>
      fetchRezeptApplicationCountOne(tenant, clinicNumber, patientId!, id!),
    enabled: !!tenant && !!clinicNumber && !!patientId && !!id && id !== 'new',
  });

  return { data, isLoading, isFetching, isError, error, refetch };
}

/** Create 申請回数 mutation. Toast 成功・失敗は呼び出し元（Modal）で表示するためここでは出さない。 */
export function useRezeptApplicationCountCreate(patientId: string) {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RezeptApplicationCountPayload) =>
      createRezeptApplicationCount(tenant, clinicNumber, patientId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rezept-application-counts'] });
    },
  });
}

/** Update 申請回数 mutation. Toast 成功・失敗は呼び出し元（Modal）で表示するためここでは出さない。 */
export function useRezeptApplicationCountUpdate(patientId: string) {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: RezeptApplicationCountPayload }) =>
      updateRezeptApplicationCount(tenant, clinicNumber, patientId, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rezept-application-counts'] });
    },
    // Suppress global "データが見つかりません。" toast on 404; the modal handles it locally
    // alongside closing the modal to avoid stacked toasts from cache-triggered detail refetches.
    meta: { suppressNotFoundToast: true },
  });
}

/** Delete 申請回数 mutation */
export function useRezeptApplicationCountDelete(patientId: string) {
  const { tenant, clinicNumber } = useClinicContext();
  const queryClient = useQueryClient();
  const toast = useAppToast();

  return useMutation({
    mutationFn: (id: number) =>
      deleteRezeptApplicationCount(tenant, clinicNumber, patientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rezept-application-counts'] });
      toast.success('削除が正常に終了しました。');
    },
    onError: err => {
      toast.error(getApiErrorMessage(err, '削除ができませんでした。'));
    },
  });
}
